import { Injectable } from '@angular/core';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NimbusSignalRHub } from '@nimbus-global/features/signalr/nimbusSignalR.hub';
import {
  IEmergencyManualPresence,
  ITenantSettingsChangedNotification,
} from '@nimbus-global/features/signalr/models/tenant-settings-changed-notification.model';
import { IAuthenticationService } from '@nimbus-library/features/authentication/services/i-authentication.service';
import { TenantRealtimeSettingsApiClient } from '@nimbus-global/features/signalr/tenant-realtime-settings.api-client';

/**
 * Routes the generic {@link ITenantSettingsChangedNotification} SignalR event to more concrete,
 * per-feature streams. This is the single FE extensibility point for tenant-level settings:
 *
 * - Add a new nullable property to {@link ITenantSettingsChangedNotification} on BE + FE.
 * - Add a private {@link Subject} below and expose it as a public `Observable`.
 * - Add one `if` branch in {@link handleNotification} to emit when the property is not `undefined`.
 *
 * State is tracked internally: the service accumulates the last known values of each property
 * so it can reconstruct the full known state even when only a partial update arrives.
 * On the first notification (state was `null`) all non-undefined properties are emitted.
 * On subsequent notifications only properties that differ from the previous state are emitted.
 */
@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class TenantRealtimeSettingsService {
  private initialized = false;
  private readonly state$ = new BehaviorSubject<ITenantSettingsChangedNotification | null>(null);

  public constructor(
    private readonly signalRHub: NimbusSignalRHub,
    private readonly authenticationService: IAuthenticationService,
    private readonly apiClient: TenantRealtimeSettingsApiClient,
  ) {}

  public initialize(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    combineLatest([
      this.signalRHub.isConnected$.pipe(distinctUntilChanged()),
      this.authenticationService.isAuthenticated$.pipe(distinctUntilChanged()),
    ])
      .pipe(
        switchMap(([isConnected, isAuthenticated]) => {
          if (!isConnected || !isAuthenticated) {
            this.state$.next(null);
            return of(null);
          }

          const { tenantId } = this.authenticationService.getUserInfo();
          return this.apiClient.getTenantRealtimeSettings(tenantId);
        }),
        tap((notification) => this.handleNotification(notification)),
        untilDestroyed(this),
      )
      .subscribe();

    this.signalRHub.onTenantSettingsChanged$.pipe(untilDestroyed(this)).subscribe((notification) => {
      this.handleNotification(notification);
    });
  }

  public getEmergencyManualPresenceMode$(): Observable<boolean> {
    return this.getEmergencyManualPresence$().pipe(
      map((x) => x.mode),
      distinctUntilChanged(),
    );
  }

  public getEmergencyManualPresenceMode(): boolean {
    return this.getEmergencyManualPresence().mode;
  }

  public getEmergencyManualPresence$(): Observable<IEmergencyManualPresence> {
    return this.state$.pipe(
      filter((x) => !!x),
      map(() => this.getEmergencyManualPresence()),
      distinctUntilChanged(isEqual),
    );
  }

  public getEmergencyManualPresence(): IEmergencyManualPresence {
    const presenceTrackingSettings = this.state$.getValue()?.presenceTrackingSettings;
    return {
      mode: presenceTrackingSettings?.enableEmergencyManualPresenceMode ?? false,
      changedAt: presenceTrackingSettings?.emergencyManualPresenceModeChangedAt ?? null,
    };
  }

  private handleNotification(notification: ITenantSettingsChangedNotification): void {
    if (!notification) {
      return;
    }

    const stateCurrent = this.state$.getValue();

    // Merge the incoming (partial) notification into the accumulated state.
    // Null group values mean "no change for this group" and must be excluded from the merge.
    const stateMerged: ITenantSettingsChangedNotification = { ...stateCurrent, ...this.omitNullAndUndefined(notification) };
    this.state$.next(stateMerged);
  }

  /** Returns a shallow copy of `obj` with all `null` and `undefined` properties removed. */
  private omitNullAndUndefined(obj: ITenantSettingsChangedNotification): Partial<ITenantSettingsChangedNotification> {
    return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null)) as Partial<ITenantSettingsChangedNotification>;
  }
}
